// src/pages/StudentsPage.js
//
// REESCRITO — ya no usa authStore.js/progressStore(mock)/gradingStore(mock)/
// chatStore(mock). Ahora usa datos reales:
//   - cursoService.getAll() para el selector de curso (scoped a la
//     institución del usuario, o global si no tiene una)
//   - aprendizajeService.getEstudiantesCurso(cursoId) para la lista de
//     estudiantes matriculados, con progreso y calificación final reales
//
// CAMBIO DE ALCANCE HONESTO: el mock separaba "promedio de quizzes" y
// "promedio de actividades" por estudiante — el backend real no expone
// ese desglose en un único endpoint (evaluacionService/entregaService
// trabajan por contenido, no agregado por estudiante). En vez de
// fabricar un promedio falso combinando llamadas, esta versión muestra
// las dos métricas que SÍ son reales y agregadas: Progreso y
// Calificación final.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Card, CardContent, Button, Avatar, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  IconButton, Paper, TextField, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, Tabs, Tab, LinearProgress, Tooltip, Breadcrumbs, Link, Menu, MenuItem,
  Divider, CircularProgress, Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Search as SearchIcon, MoreVert as MoreVertIcon,
  Chat as ChatIcon, Grade as GradeIcon, Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AppContext';
import { cursoService, aprendizajeService, meService } from '../services/api';

function StudentsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [cursos, setCursos] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [cargandoCursos, setCargandoCursos] = useState(true);
  const [estudiantes, setEstudiantes] = useState([]);
  const [cargandoEstudiantes, setCargandoEstudiantes] = useState(false);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuStudent, setMenuStudent] = useState(null);

  useEffect(() => {
    (async () => {
      setCargandoCursos(true);
      try {
        const misInstituciones = await meService.getMisInstituciones();
        const institutionId = misInstituciones?.[0]?.id;
        const { data } = await cursoService.getAll(institutionId ? { institutionId, limit: 50 } : { limit: 50 });
        setCursos(data);
        if (data.length > 0) setSelectedCourse(data[0].id);
      } catch (e) {
        setError(e?.message || 'No se pudieron cargar los cursos.');
      } finally {
        setCargandoCursos(false);
      }
    })();
  }, []);

  const cargarEstudiantes = useCallback(async () => {
    if (!selectedCourse) { setEstudiantes([]); return; }
    setCargandoEstudiantes(true);
    setError(null);
    try {
      const { data } = await aprendizajeService.getEstudiantesCurso(selectedCourse, { limit: 200 });
      setEstudiantes(data);
    } catch (e) {
      setError(e?.message || 'No se pudieron cargar los estudiantes.');
      setEstudiantes([]);
    } finally {
      setCargandoEstudiantes(false);
    }
  }, [selectedCourse]);

  useEffect(() => { cargarEstudiantes(); }, [cargarEstudiantes]);

  const enrichedStudents = estudiantes.map((e) => ({
    id: e.studentId, name: e.studentName || 'Estudiante',
    avatar: null, enrolledAt: e.enrolledAt,
    progress: e.progress || 0, finalGrade: e.finalGrade,
    status: e.status,
  }));

  const filteredStudents = enrichedStudents.filter((student) =>
    student.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedStudents = [...filteredStudents].sort((a, b) => {
    let comparison = 0;
    switch (orderBy) {
      case 'name': comparison = a.name.localeCompare(b.name); break;
      case 'progress': comparison = (a.progress || 0) - (b.progress || 0); break;
      case 'finalGrade': comparison = (a.finalGrade || 0) - (b.finalGrade || 0); break;
      default: comparison = 0;
    }
    return order === 'asc' ? comparison : -comparison;
  });

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleOpenChat = (student) => {
    navigate('/instructor/chat', { state: { targetUserId: student.id, targetUserName: student.name } });
  };

  const handleViewDetail = (student) => {
    setSelectedStudent(student);
    setDetailDialog(true);
  };

  const getGradeColor = (grade) => {
    if (grade === null || grade === undefined) return 'text.disabled';
    if (grade >= 80) return 'success.main';
    if (grade >= 60) return 'warning.main';
    return 'error.main';
  };

  const getProgressColor = (progress) => {
    if (progress >= 75) return 'success';
    if (progress >= 50) return 'info';
    if (progress >= 25) return 'warning';
    return 'error';
  };

  const stats = {
    totalStudents: enrichedStudents.length,
    avgProgress: enrichedStudents.length > 0
      ? Math.round(enrichedStudents.reduce((sum, s) => sum + (s.progress || 0), 0) / enrichedStudents.length)
      : 0,
    avgGrade: enrichedStudents.filter((s) => s.finalGrade != null).length > 0
      ? Math.round(enrichedStudents.filter((s) => s.finalGrade != null).reduce((sum, s) => sum + s.finalGrade, 0) / enrichedStudents.filter((s) => s.finalGrade != null).length)
      : 0,
    atRisk: enrichedStudents.filter((s) => s.progress < 25 || (s.finalGrade != null && s.finalGrade < 60)).length,
  };

  const courseName = cursos.find((c) => c.id === selectedCourse)?.title || '';

  if (cargandoCursos) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="xl">
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/instructor'); }}
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            Dashboard
          </Link>
          <Typography color="text.primary">Estudiantes</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate('/instructor')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700}>
            👥 Mis Estudiantes
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {cursos.length === 0 ? (
          <Alert severity="info">No hay cursos disponibles todavía.</Alert>
        ) : (
          <>
            {cursos.length > 1 && (
              <Tabs value={selectedCourse} onChange={(e, v) => setSelectedCourse(v)} sx={{ mb: 3 }} variant="scrollable" scrollButtons="auto">
                {cursos.map((c) => <Tab key={c.id} value={c.id} label={c.title} />)}
              </Tabs>
            )}

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Card><CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" fontWeight={700} color="primary.main">{stats.totalStudents}</Typography>
                  <Typography variant="body2" color="text.secondary">Estudiantes</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card><CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" fontWeight={700} color="info.main">{stats.avgProgress}%</Typography>
                  <Typography variant="body2" color="text.secondary">Progreso Promedio</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card><CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" fontWeight={700} color="success.main">{stats.avgGrade}%</Typography>
                  <Typography variant="body2" color="text.secondary">Calificación Promedio</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card><CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" fontWeight={700} color="error.main">{stats.atRisk}</Typography>
                  <Typography variant="body2" color="text.secondary">En Riesgo</Typography>
                </CardContent></Card>
              </Grid>
            </Grid>

            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  placeholder="Buscar estudiante..." size="small" value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
                  sx={{ minWidth: 300 }}
                />
              </CardContent>
            </Card>

            <Card>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={() => handleSort('name')}>
                          Estudiante
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center">
                        <TableSortLabel active={orderBy === 'progress'} direction={orderBy === 'progress' ? order : 'asc'} onClick={() => handleSort('progress')}>
                          Progreso
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center">
                        <TableSortLabel active={orderBy === 'finalGrade'} direction={orderBy === 'finalGrade' ? order : 'asc'} onClick={() => handleSort('finalGrade')}>
                          Calificación final
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center">Estado</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cargandoEstudiantes ? (
                      <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                    ) : sortedStudents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <SchoolIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                          <Typography color="text.secondary">No hay estudiantes matriculados en este curso</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedStudents.map((student) => (
                        <TableRow key={student.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleViewDetail(student)}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar src={student.avatar}>{student.name?.[0]}</Avatar>
                              <Typography variant="body2" fontWeight={600}>{student.name}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress variant="determinate" value={student.progress || 0} color={getProgressColor(student.progress)} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                              <Typography variant="body2" fontWeight={500} sx={{ minWidth: 40 }}>{student.progress || 0}%</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {student.finalGrade != null ? (
                              <Chip label={`${student.finalGrade}%`} size="small" sx={{ bgcolor: getGradeColor(student.finalGrade), color: 'white', fontWeight: 600 }} />
                            ) : (
                              <Typography variant="caption" color="text.disabled">Sin datos</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {student.progress < 25 || (student.finalGrade != null && student.finalGrade < 60) ? (
                              <Chip icon={<TrendingDownIcon />} label="En riesgo" size="small" color="error" />
                            ) : student.progress >= 75 ? (
                              <Chip icon={<TrendingUpIcon />} label="Excelente" size="small" color="success" />
                            ) : (
                              <Chip label="Normal" size="small" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Ver detalle">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewDetail(student); }}><VisibilityIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Enviar mensaje">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenChat(student); }}><ChatIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Más opciones">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); setMenuStudent(student); }}><MoreVertIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </>
        )}

        <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar src={selectedStudent?.avatar} sx={{ width: 56, height: 56 }}>{selectedStudent?.name?.[0]}</Avatar>
              <Typography variant="h6">{selectedStudent?.name}</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>📊 Rendimiento en {courseName}</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} color="primary.main">{selectedStudent?.progress || 0}%</Typography>
                  <Typography variant="caption" color="text.secondary">Progreso del curso</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: getGradeColor(selectedStudent?.finalGrade) }}>
                    {selectedStudent?.finalGrade != null ? `${selectedStudent.finalGrade}%` : '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Calificación final</Typography>
                </Paper>
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>📅 Información de inscripción</Typography>
            <Typography variant="body2" color="text.secondary">
              Inscrito desde: {selectedStudent?.enrolledAt ? new Date(selectedStudent.enrolledAt).toLocaleDateString() : 'N/A'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailDialog(false)}>Cerrar</Button>
            <Button variant="outlined" startIcon={<ChatIcon />} onClick={() => { setDetailDialog(false); handleOpenChat(selectedStudent); }}>
              Enviar mensaje
            </Button>
            <Button variant="contained" startIcon={<GradeIcon />} onClick={() => { setDetailDialog(false); navigate('/instructor/grading'); }}>
              Ver calificaciones
            </Button>
          </DialogActions>
        </Dialog>

        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={() => { handleViewDetail(menuStudent); setMenuAnchor(null); }}>
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />Ver detalle
          </MenuItem>
          <MenuItem onClick={() => { handleOpenChat(menuStudent); setMenuAnchor(null); }}>
            <ChatIcon fontSize="small" sx={{ mr: 1 }} />Enviar mensaje
          </MenuItem>
        </Menu>
      </Container>
    </Box>
  );
}

export default StudentsPage;
